import styled from 'styled-components';
import { Progress, Spin } from 'antd';
import { LoaderIcon, SpinnerIcon } from '../utils/svgIcons';
import { useTranslation } from 'react-i18next';
import { customLogger } from '../utils/common';
import { useEffect, type JSX } from 'react';

const LoaderBg = styled.div`
  position: fixed;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #00000080;
  backdrop-filter: blur(5px);
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  height: -webkit-fill-available;
  .inner-container {
    display: inline-flex;
    flex-direction: row;
    gap: 8px;
  }
  .description {
    color: #f3f4f6;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: -0.105px;

    b {
      font-weight: 700;
      font-size: 18px;
    }
  }
  .heading {
    color: #fff;
    font-size: 20px;
    font-weight: 700;
    line-height: 28px;
    letter-spacing: -0.15px;
    margin: 0;
  }
  .loaderBox {
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.7;
      transform: scale(1.05);
    }
  }

  @keyframes float {
    0%,
    100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-10px);
    }
  }

  .loader-icon-animated {
    animation:
      pulse 2s ease-in-out infinite,
      float 3s ease-in-out infinite;
  }

  @keyframes dots {
    0%,
    20% {
      content: '.';
    }
    40% {
      content: '..';
    }
    60%,
    100% {
      content: '...';
    }
  }

  .loading-dots {
    display: inline-block;
    margin-left: 2px;
    width: 20px;
    text-align: left;
  }

  .loading-dots::after {
    content: '.';
    animation: dots 1.5s steps(1) infinite;
  }
`;

type LoaderProps = {
  heading?: string;
  description?: JSX.Element | string;
  progress?: {
    loaded: number;
    total: number;
  };
  trialCount?: number;
};

export const Spinner = ({ style }) => {
  return (
    <Spin
      indicator={<SpinnerIcon data-testid='icon' spin style={{ width: 24, height: 24 }} />}
      style={style}
      data-testid='spinner'
    />
  );
};

const Loader = ({
  heading = 'PA_LoadingApplication',
  description,
  progress = null,
  trialCount = 0
}: LoaderProps) => {
  const { t } = useTranslation();
  const resolvedDescription = description ?? <span>{t('PA_LoadingApplicationDescription')}</span>;
  const loaded = progress?.loaded ?? 0;
  const total = progress?.total ?? 0;
  const percent = false;
  // total && total > 0 ? Math.min(100, Math.round((loaded * 100) / total)) : undefined;
  const humanBytes = (n: number) => {
    if (!n) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let i = 0;
    let v = n;
    while (v >= 1024 && i < units.length - 1) {
      v /= 1024;
      i++;
    }
    return `${v.toFixed(1)} ${units[i]}`;
  };
  // const env = import.meta.env.PA_ENV_NAME as string;

  useEffect(() => {
    // Cleanup not needed for progress or total as they are constants and props
    return () => {};
  }, []);

  customLogger('Loader rendered with progress:', { progress, percent });
  return (
    <LoaderBg data-testid='blur-bg'>
      <div className='inner-container'>
        {/* <Spinner style={{ marginTop: '2px' }} /> */}
        <div className='loaderBox'>
          <div className='loader-icon-animated'>
            <LoaderIcon />
          </div>
          <h3 className='heading' data-testid='heading'>
            {/* {loaded ? humanBytes(loaded) : null} */}
            {t(heading)}
            <span className='loading-dots'></span>
          </h3>
          <span className='description' data-testid='desc'>
            {trialCount > 0 ? (
              <span data-testid='trial-count'>
                Analyzing data from <b>{trialCount.toLocaleString()}</b> {t('PA_localized_trials')}.{' '}
                {t('PA_Please_wait_a_moment')}.
              </span>
            ) : (
              resolvedDescription || null
            )}
          </span>

          {percent ? (
            <>
              <Progress percent={percent} showInfo={false} strokeColor='#1890ff' />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: 6,
                  color: '#f3f4f6',
                  fontSize: 12
                }}
              >
                <span>{humanBytes(loaded)}</span>
                <span>{total > 0 ? humanBytes(total) : t('PA_Unknown')}</span>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </LoaderBg>
  );
};

export default Loader;
